/**
 * @file Loading state.
 *
 * Fetches assets, generates the dungeon, builds the level, then hands over to
 * gameplay. Play never starts until generation succeeds.
 */

import { GameState } from './GameState.js';
import { GameStates } from './StateMachine.js';
import { buildLevel } from '../../world/Level.js';
import { Events } from '../EventBus.js';
import { weaponModelManifest } from '../../config/WeaponArt.js';
import { characterModelManifest } from '../../config/CharacterArt.js';

export class LoadingState extends GameState {
  /**
   * @param {import('../Context.js').GameContext} ctx Shared game context.
   * @param {import('./StateMachine.js').StateMachine} machine Owning machine.
   * @param {import('../AssetLoader.js').AssetLoader} assets Asset loader.
   */
  constructor(ctx, machine, assets) {
    super(ctx, machine);
    /** @type {import('../AssetLoader.js').AssetLoader} @private */
    this._assets = assets;
    /** @type {boolean} @private */
    this._busy = false;
  }

  /** @returns {void} */
  enter() {
    this._busy = true;
    this._load();
  }

  /**
   * @returns {Promise<void>}
   * @private
   */
  async _load() {
    // Weapon and character models must be in hand before the level exists: the
    // first mesh a prefab builds is the one that decides whether it is real
    // art or the procedural fallback. A failed download is not fatal — the
    // fallback is.
    try {
      await this._assets.load(weaponModelManifest());
    } catch (error) {
      console.warn('[LoadingState] weapon models failed to load; using fallbacks', error);
    }
    this.ctx.weaponModels?.adopt(this._assets);

    try {
      await this._assets.load(characterModelManifest());
    } catch (error) {
      console.warn('[LoadingState] character models failed to load; using fallbacks', error);
    }
    this.ctx.characterModels?.adopt(this._assets);

    // Yield one frame so the HUD can paint "Generating dungeon…"
    await new Promise((resolve) => requestAnimationFrame(() => resolve()));

    let ok = false;
    let attempts = 0;
    const maxAttempts = this.ctx.config.dungeon?.maxGenAttempts ?? 5;

    while (!ok && attempts < maxAttempts) {
      attempts++;
      // On retry, nudge the level seed without changing run identity.
      if (attempts > 1) {
        this.ctx.levelSeed = (Math.imul(this.ctx.levelSeed >>> 0, 9301) + 49297) >>> 0;
      }
      ok = buildLevel(this.ctx);
    }

    this._busy = false;

    if (!ok) {
      console.error('[LoadingState] dungeon generation failed; returning to menu');
      if (this.machine.is(GameStates.LOADING)) this.machine.change(GameStates.MENU);
      return;
    }

    const arena = this.ctx.arena;
    this.ctx.events.emit(Events.LEVEL_READY, {
      levelIndex: this.ctx.levelIndex,
      seed: arena?.seed ?? this.ctx.levelSeed,
      name: arena?.name ?? null,
      themeKey: arena?.themeKey ?? null
    });

    if (this.machine.is(GameStates.LOADING)) this.machine.change(GameStates.PLAYING);
  }

  /** @returns {boolean} True while assets/level are still resolving. */
  get busy() {
    return this._busy;
  }
}
